import { FC, FocusEventHandler, useEffect, useMemo, useState } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useDataSubjectsQuery } from '../datasubject';
import { useDebounce } from '../common/useDebounce';
import {
  FormAutoCompleteOption,
  FormField,
  FormFieldProps,
  useGetIdFromName,
} from '../common/form';
import translations from './DataSubjectAutoComplete.translations.json';
import { Controller, useFormContext } from 'react-hook-form';
import { AutoComplete, Input } from 'antd';
import { SampleDataSubjectValidFormDataType } from './schema';

const { addTranslations, useTranslation } = i18nNsFactory(
  'data-subject-auto-complete'
);
addTranslations(translations);

export type DataSubjectAutoCompleteProps = FormFieldProps;

export const DataSubjectAutoComplete: FC<DataSubjectAutoCompleteProps> = (
  props
) => {
  const { name } = props;
  const [search, setSearch] = useState('');

  const { t } = useTranslation();
  const debouncedSearch = useDebounce(search, 300);
  const { setFocus } = useFormContext();

  const rowIndex = Number(name.split('.')[1]);
  const { getValues } = useFormContext<{
    samples: SampleDataSubjectValidFormDataType[];
  }>();

  const { data, refetch, isFetching } = useDataSubjectsQuery(
    {
      search: debouncedSearch,
      size: 100,
    },
    { enabled: debouncedSearch.length > 2 }
  );
  const dataSubjects = useMemo(() => {
    const samples = getValues('samples');
    const existingIdSet = new Set(
      samples?.map((value, index) =>
        index === rowIndex ? 0 : value.dataSubject?.id
      )
    );
    return data?.content.filter(
      (dataSubject) => !existingIdSet.has(dataSubject.id)
    );
  }, [data, rowIndex, getValues]);

  const handleSelect = (
    value: number,
    onChange: (...event: any[]) => void,
    setDisplayValue: (val: string) => void
  ) => {
    const { id, name, code } =
      dataSubjects?.find(({ id }) => id === value) || {};
    const displayName = `${code} - ${name}`;
    if (id) {
      onChange({ id, name: displayName });
      setDisplayValue(displayName);
    }
  };

  const options: FormAutoCompleteOption[] = useMemo(
    () =>
      debouncedSearch.length > 2 && dataSubjects?.length && !isFetching
        ? dataSubjects.map((item) => ({
            label: `${item.code} - ${item.name}`,
            value: item.id,
          }))
        : [],
    [dataSubjects, debouncedSearch.length, isFetching]
  );

  useEffect(() => {
    setFocus(name, { shouldSelect: true });
  }, [setFocus, name]);

  return (
    <FormField {...props}>
      <CustomAutoComplete
        name={name}
        options={options}
        isLoading={isFetching}
        onSelect={handleSelect}
        onSearch={setSearch}
        onSearchClick={() => {
          if (debouncedSearch.length > 2) {
            refetch();
          }
        }}
        autoFocus
        placeholder={t('placeholder.search')}
      />
    </FormField>
  );
};

type CustomAutoCompleteProps = {
  name: string;
  options?: FormAutoCompleteOption<number>[];
  isLoading?: boolean;
  onSearch?: (value: string) => void;
  onSelect?: (
    value: number,
    onChange: (...event: any[]) => void,
    setDisplayValue: (val: string) => void
  ) => void;
  onSearchClick?: () => void;
  placeholder?: string;
  onBlur?: FocusEventHandler<HTMLElement>;
  autoFocus?: boolean;
};

const CustomAutoComplete: FC<CustomAutoCompleteProps> = ({
  name,
  options,
  isLoading,
  onSearch,
  onSelect,
  onSearchClick,
  placeholder,
  onBlur,
  autoFocus,
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const getIdFromName = useGetIdFromName();
  const id = getIdFromName(name);
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const { onChange } = field;

        return (
          <AutoComplete
            id={id}
            value={displayValue || field.value.name}
            options={options}
            onChange={(value) => setDisplayValue(value)}
            onSearch={onSearch}
            onSelect={(value: any) =>
              onSelect?.(value, onChange, setDisplayValue)
            }
            onBlur={onBlur}
            autoFocus={autoFocus}
          >
            <Input.Search
              id={id}
              loading={isLoading}
              onSearch={onSearchClick}
              placeholder={placeholder}
              onBlur={onBlur}
              ref={field.ref}
            />
          </AutoComplete>
        );
      }}
    ></Controller>
  );
};
